import React, {
  useState,
  Fragment,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from 'react'
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  MouseSensor,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'

import { CSS } from '@dnd-kit/utilities'

import Head from 'next/head'
import { VenueLayout } from '../../../../components/old/Layout'

import {
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  SquaresPlusIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid'
import {
  FailureNotification,
  SuccessNotification,
} from '../../../../components/old/Notification'
import ButtonLoader from 'app/design/animations/ButtonLoader'
import { useRouter } from 'next/router'
import {
  Arena,
  ArenaSection,
  ArenaSectionOrderingTypes,
  ArenaSectionSeat,
  ArenaSectionSeatTypes,
} from 'app/types/arena'
import { classNames } from 'app/utils/classes'
import { useTranslation } from 'react-i18next'
import { Price, Ticket, User } from 'app/types/authorization'
import { StandardAPIRequest } from 'app/types/api'
import { clientGetIdToken, clientUserFromCognito } from 'app/utils/cognito'
import Pricing from '../../../../components/Pricing'
import { Dialog, RadioGroup, Switch, Transition } from '@headlessui/react'
import OrganizerArena from '../../../../components/old/OrganizerArena'
import LivedayLoader from '../../../../components/old/animations/LivedayLoader'
import { AuthContext } from 'app/utils/context'
import Link from 'next/link'
import { captureException } from '@sentry/nextjs'
import GenericTable from 'app/design/table'
import PrimaryVenueNavigation from 'app/design/navigation'

export const exportQRCode = async (data: string) => {
  let response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/qr/export.png`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await clientGetIdToken()}`,
      },
      body: JSON.stringify({
        data,
      }),
    }
  )

  if (!response.ok) {
    throw new Error('Network response was not ok')
  }

  // Handle the Blob
  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)

  // Create an anchor to trigger the download
  const a = document.createElement('a')
  a.href = url
  a.download = 'export.png' // Specify the filename you want to download as
  document.body.appendChild(a) // Append the anchor to the body
  a.click() // Simulate click on the anchor

  // Clean up
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

interface WheelChairIconProps {
  large?: boolean
}

interface StairsIconProps {
  className?: string
}

export const StairsIcon = ({ className = '' }: StairsIconProps) => (
  <svg
    className={classNames('mx-auto', className)}
    width="35"
    height="34"
    viewBox="0 0 35 34"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M34.2398 22H0.62915V33.4408H34.2398V22Z" fill="#6B7280" />
    <path
      d="M34.2398 0.401733H0.62915V11.8425H34.2398V0.401733Z"
      fill="#6B7280"
    />
  </svg>
)

export const WheelChairIcon = ({ large = false }: WheelChairIconProps) => (
  <svg
    width="18"
    height="22"
    viewBox="0 0 18 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={classNames(
      large ? '-ml-0.5 h-6 w-6' : '-ml-1 h-4 w-4',
      '-mt-0.5 block text-black'
    )}
  >
    <path
      d="M7.95678 4.33597C9.15289 4.33597 10.1247 3.36532 10.1247 2.16801C10.1247 0.97033 9.15289 0 7.95678 0C6.75929 0 5.78833 0.97033 5.78833 2.16801C5.78833 3.36532 6.75929 4.33597 7.95678 4.33597Z"
      fill="currentColor"
    />
    <path
      d="M11.307 16.6211C10.7309 18.6214 8.88685 20.0905 6.7035 20.0905C4.05952 20.0905 1.90936 17.9402 1.90936 15.297C1.90936 13.3405 3.08973 11.6564 4.77425 10.9123V8.88013C2.01561 9.71103 0 12.2718 0 15.297C0 18.9929 3.00713 22.0001 6.70346 22.0001C9.39717 22.0001 11.719 20.3992 12.7836 18.1021L12.0267 16.615C11.8142 16.6167 11.5681 16.619 11.307 16.6211Z"
      fill="currentColor"
    />
    <path
      d="M17.6234 19.1945L14.7195 13.4853C14.5028 13.0581 14.0646 12.7893 13.5853 12.7893H9.86602V11.9411H13.067C13.3867 11.9411 13.6553 11.7741 13.8289 11.5334C13.9416 11.3766 14.0219 11.1945 14.0219 10.9862C14.0219 10.4591 13.5949 10.0315 13.067 10.0315H11.1386H9.86602V7.15487C9.86602 6.36372 9.27942 5.24524 7.95707 5.24524C6.90244 5.24524 6.04749 6.10033 6.04749 7.15487V13.3873C6.04749 14.4631 6.91962 15.3352 7.99548 15.3352H11.4953H12.8048L15.3548 20.3488C15.5789 20.7904 16.0261 21.0452 16.4902 21.0452C16.6847 21.0452 16.8819 21.0008 17.0663 20.9064C17.6938 20.5881 17.9431 19.8212 17.6234 19.1945Z"
      fill="currentColor"
    />
  </svg>
)
interface SeatGridProps {
  eventSk: string | string[] | undefined
  seasonTicketSk: string | string[] | undefined
  rows: ArenaSectionSeat[][] | undefined
  setRows: React.Dispatch<React.SetStateAction<ArenaSectionSeat[][]>>
  newRows: ArenaSectionSeatTypes[][] | undefined
  setNewRows: React.Dispatch<React.SetStateAction<ArenaSectionSeatTypes[][]>>
  tickets: ArenaSectionSeatTypes[][] | undefined
  setTickets: React.Dispatch<React.SetStateAction<ArenaSectionSeatTypes[][]>>
  deleteSeat: (pk: string, sk: string) => void
  buyTicketFree: (ticket: Ticket, status: string) => void
  changeSeatStatus: (seat: ArenaSectionSeat, status: string | null) => void
  setHandicap: (ticket: Ticket, handicap: boolean) => void
  setHandicapForSeat: (seat: ArenaSectionSeat, handicap: boolean) => void
  mode: Modes
  // For seat selection
  selectedSeats: { rowIndex: number; seatIndex: number }[]
  setSelectedSeats: React.Dispatch<
    React.SetStateAction<{ rowIndex: number; seatIndex: number }[]>
  >
  isSelecting: boolean
  setIsSelecting: React.Dispatch<React.SetStateAction<boolean>>
  onSelectionComplete?: () => void
}

const SeatGrid = ({
  eventSk,
  seasonTicketSk,
  rows,
  newRows,
  setNewRows,
  tickets,
  setTickets,
  deleteSeat,
  buyTicketFree,
  changeSeatStatus,
  setHandicap,
  setHandicapForSeat,
  mode,
  selectedSeats,
  setSelectedSeats,
  isSelecting,
  setIsSelecting,
  onSelectionComplete,
}: SeatGridProps) => {
  const { state } = useContext(AuthContext)
  const { t } = useTranslation('common')
  const [activeTicket, setActiveTicket] = useState<Ticket | undefined>()
  const [activeSeat, setActiveSeat] = useState<ArenaSectionSeat | undefined>()
  const [owner, setOwner] = useState<User | null>(null)

  // State for selection box
  const [selectionStart, setSelectionStart] = useState<{
    x: number
    y: number
  } | null>(null)
  const [selectionEnd, setSelectionEnd] = useState<{
    x: number
    y: number
  } | null>(null)
  const gridContainerRef = useRef<HTMLDivElement>(null)

  // Selection handler functions
  const handleContainerMouseDown = (event: React.MouseEvent) => {
    if (mode === 'choose') {
      // Prevent browser text selection
      event.preventDefault()

      // Start selection from anywhere in the grid
      setIsSelecting(true)
      setSelectionStart({ x: event.clientX, y: event.clientY })
      setSelectionEnd({ x: event.clientX, y: event.clientY })
      setSelectedSeats([])
    }
  }

  const handleContainerMouseMove = (event: React.MouseEvent) => {
    if (isSelecting && mode === 'choose' && selectionStart) {
      // Prevent browser text selection
      event.preventDefault()

      // Update end position
      setSelectionEnd({ x: event.clientX, y: event.clientY })

      // Find all seats in the selection rectangle
      updateSelectedSeatsFromSelection(event.clientX, event.clientY)
    }
  }

  const updateSelectedSeatsFromSelection = (
    currentX: number,
    currentY: number
  ) => {
    if (!selectionStart) return

    // Calculate selection rectangle
    const left = Math.min(selectionStart.x, currentX)
    const right = Math.max(selectionStart.x, currentX)
    const top = Math.min(selectionStart.y, currentY)
    const bottom = Math.max(selectionStart.y, currentY)

    // Clear previous selection
    const newSelectedSeats: { rowIndex: number; seatIndex: number }[] = []

    // Check all seat elements
    document.querySelectorAll('[data-seat-element]').forEach((element) => {
      const rect = element.getBoundingClientRect()
      const rowIndex = parseInt(element.getAttribute('data-row-index') || '-1')
      const seatIndex = parseInt(
        element.getAttribute('data-seat-index') || '-1'
      )

      if (rowIndex >= 0 && seatIndex >= 0) {
        // Check if this seat's center is inside selection rectangle
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2

        if (
          centerX >= left &&
          centerX <= right &&
          centerY >= top &&
          centerY <= bottom
        ) {
          // Only add seats or tickets (not spaces or stairs)
          const rowData =
            eventSk || seasonTicketSk
              ? tickets?.[rowIndex]
              : newRows?.[rowIndex]
          const seatData = rowData?.[seatIndex]
          if (seatData?.Seat || seatData?.Ticket) {
            newSelectedSeats.push({ rowIndex, seatIndex })
          }
        }
      }
    })

    setSelectedSeats(newSelectedSeats)
  }

  const handleContainerMouseUp = (event: React.MouseEvent) => {
    if (mode === 'choose') {
      // Prevent browser text selection
      event.preventDefault()

      // Update selection one last time
      if (selectionStart && isSelecting) {
        updateSelectedSeatsFromSelection(event.clientX, event.clientY)
      }

      setIsSelecting(false)
      setSelectionStart(null)
      setSelectionEnd(null)

      // Show price modal if seats are selected
      if (selectedSeats.length > 0 && onSelectionComplete) {
        onSelectionComplete()
      }
    }
  }

  // Click handlers for individual seats - still needed for basic selection
  const handleSeatMouseDown = (rowIndex: number, seatIndex: number) => {
    if (mode === 'choose') {
      setIsSelecting(true)
      setSelectedSeats([{ rowIndex, seatIndex }])

      // Set initial selection point using the first selection
      const element = document.querySelector(
        `[data-row-index="${rowIndex}"][data-seat-index="${seatIndex}"]`
      )
      if (element) {
        const rect = element.getBoundingClientRect()
        setSelectionStart({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        })
        setSelectionEnd({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        })
      }
    }
  }

  const handleSeatMouseEnter = (rowIndex: number, seatIndex: number) => {
    if (isSelecting && mode === 'choose') {
      // Add to selection if not already selected
      if (
        !selectedSeats.some(
          (seat) => seat.rowIndex === rowIndex && seat.seatIndex === seatIndex
        )
      ) {
        setSelectedSeats([...selectedSeats, { rowIndex, seatIndex }])

        // Update end point of selection
        const element = document.querySelector(
          `[data-row-index="${rowIndex}"][data-seat-index="${seatIndex}"]`
        )
        if (element && selectionStart) {
          const rect = element.getBoundingClientRect()
          setSelectionEnd({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          })
        }
      }
    }
  }

  // Prevent text selection during drag
  useEffect(() => {
    if (isSelecting) {
      document.body.style.userSelect = 'none'
    } else {
      document.body.style.userSelect = ''
    }

    // Cancel selection on escape key
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSelecting(false)
        setSelectionStart(null)
        setSelectionEnd(null)
        setSelectedSeats([])
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.userSelect = ''
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSelecting, setIsSelecting, setSelectedSeats])

  useEffect(() => {
    if (activeTicket === undefined) {
      setOwner(null)
    }
  }, [activeTicket])

  useEffect(() => {
    const fetchData = async () => {
      if (activeTicket?.owner !== undefined && activeTicket.owner !== null) {
        try {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/user/${activeTicket.owner}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          const res: StandardAPIRequest<User> = await response.json()
          if (res.error) {
            throw new Error(res.error.message)
          }
          if (res.data) {
            setOwner(res.data ?? null)
          }
        } catch (error: any) {
          captureException(error)
        }
      }
    }

    fetchData()
  }, [activeTicket?.owner])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
    useSensor(MouseSensor)
  )

  const handleDragEnd = (event: any) => {
    const { active, over } = event

    if (newRows !== undefined) {
      if (active.id !== over.id) {
        const oldIndex = newRows.findIndex(
          (_row, idx) => `${idx}` === active.id
        )
        const newIndex = newRows.findIndex((_row, idx) => `${idx}` === over.id)
        if (newRows.length > 0) {
          setNewRows((currentRows) =>
            arrayMove(currentRows ?? [], oldIndex, newIndex)
          )
        }
      }
    }

    if (tickets !== undefined) {
      if (active.id !== over.id) {
        const oldIndex = tickets.findIndex(
          (_row, idx) => `${idx}` === active.id
        )
        const newIndex = tickets.findIndex((_row, idx) => `${idx}` === over.id)
        if (tickets.length > 0) {
          setTickets((currentRows) =>
            arrayMove(currentRows ?? [], oldIndex, newIndex)
          )
        }
      }
    }
  }

  // Calculate selection box position if selection is active
  const selectionBox = useMemo(() => {
    if (!selectionStart || !selectionEnd || !isSelecting) {
      return null
    }

    const left = Math.min(selectionStart.x, selectionEnd.x)
    const top = Math.min(selectionStart.y, selectionEnd.y)
    const width = Math.abs(selectionEnd.x - selectionStart.x)
    const height = Math.abs(selectionEnd.y - selectionStart.y)

    return { left, top, width, height }
  }, [selectionStart, selectionEnd, isSelecting])

  return (
    <div
      className="relative overflow-x-scroll"
      ref={gridContainerRef}
      onMouseDown={handleContainerMouseDown}
      onMouseMove={handleContainerMouseMove}
      onMouseUp={handleContainerMouseUp}
    >
      {/* Selection box */}
      {selectionBox && (
        <div
          className="pointer-events-none absolute z-10 border-2 border-blue-500 bg-blue-200 bg-opacity-20"
          style={{
            left: selectionBox.left,
            top: selectionBox.top,
            width: selectionBox.width,
            height: selectionBox.height,
            position: 'fixed',
          }}
        />
      )}
      <Transition.Root show={activeTicket !== undefined} as={Fragment}>
        <Dialog
          as="div"
          className={`${state.theme} relative z-10`}
          onClose={() => setActiveTicket(undefined)}
        >
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col divide-y divide-gray-800 bg-gray-800 shadow-xl">
                      <div className="h-0 flex-1 overflow-y-auto">
                        <div className="bg-gray-800 px-4 py-6 sm:px-6">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-base font-semibold leading-6 text-white">
                              {t('ticket-information')}
                            </Dialog.Title>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="relative rounded-md bg-gray-700 text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                onClick={() => setActiveTicket(undefined)}
                              >
                                <span className="absolute -inset-2.5" />
                                <span className="sr-only">{t('close')}</span>
                                <XMarkIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                        {activeTicket !== undefined && (
                          <div className="flex flex-1 flex-col justify-between border-t border-gray-200 pt-2">
                            <div className="divide-y px-4 sm:px-6">
                              <p className="text-gray-200">
                                {t('status')}:{' '}
                                <span className="font-semibold">
                                  {activeTicket.status}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('section')}:{' '}
                                <span className="font-semibold">
                                  {activeTicket.section_name}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('row')}:{' '}
                                <span className="font-semibold">
                                  {activeTicket.row}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('seat')}:{' '}
                                <span className="font-semibold">
                                  {activeTicket.seat}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('wheelchair-seat')}:{' '}
                                <span className="font-semibold">
                                  {activeTicket.handicap_seat
                                    ? t('yes')
                                    : t('no')}
                                </span>
                                <ArrowsRightLeftIcon
                                  onClick={() => {
                                    setHandicap(
                                      activeTicket,
                                      !activeTicket.handicap_seat
                                    )
                                  }}
                                  className={classNames(
                                    'text-brand inline-block h-5 w-7 cursor-pointer px-1'
                                  )}
                                  aria-hidden="true"
                                />
                              </p>
                              {owner !== null && (
                                <React.Fragment>
                                  <p className="text-lg font-semibold text-gray-200">
                                    {t('ticket-owner')}
                                  </p>
                                  {(owner.first_name || owner.last_name) && (
                                    <p className="text-gray-200">
                                      {t('name')}:{' '}
                                      <span className="font-semibold">
                                        {`${owner.first_name} ${owner.last_name}`}
                                      </span>
                                    </p>
                                  )}
                                  {owner.email && (
                                    <p className="text-gray-200">
                                      {t('email-address')}:{' '}
                                      <span className="font-semibold">
                                        {owner.email}
                                      </span>
                                    </p>
                                  )}
                                  {owner.email && (
                                    <p className="text-gray-200">
                                      {t('phone-number')}:{' '}
                                      <span className="font-semibold">
                                        {owner.phone_number}
                                      </span>
                                    </p>
                                  )}
                                </React.Fragment>
                              )}
                              {activeTicket.status !== 'Open' && (
                                <React.Fragment>
                                  <a
                                    href={`https://api.liveday.se/ticket/ticket/${activeTicket.ticket_key}/free.pdf`}
                                    className="mt-3 text-sm text-gray-200 hover:underline"
                                  >
                                    {t('link-to-ticket-pdf')}
                                  </a>
                                  <span className="mx-1 text-white">|</span>
                                  <div
                                    onClick={() => {
                                      exportQRCode(activeTicket.ticket_key)
                                    }}
                                    className="inline-flex cursor-pointer items-center rounded-lg p-0.5 text-center text-sm font-medium text-gray-500 hover:text-gray-800 focus:outline-none dark:text-gray-400 dark:hover:text-gray-100"
                                  >
                                    {t('download-qr-code')}
                                  </div>
                                </React.Fragment>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 justify-end p-4">
                        {activeTicket?.status === 'Open' && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeTicket !== undefined) {
                                buyTicketFree(activeTicket, 'Reserved')
                              }
                            }}
                          >
                            {t('reserve-ticket')}
                          </button>
                        )}
                        {activeTicket?.status === 'Reserved' && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeTicket !== undefined) {
                                buyTicketFree(activeTicket, 'Open')
                              }
                            }}
                          >
                            {t('release-reservation')}
                          </button>
                        )}
                        {activeTicket?.status === 'Booked' && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeTicket !== undefined) {
                                buyTicketFree(activeTicket, 'Open')
                              }
                            }}
                          >
                            {t('release-ticket')}
                          </button>
                        )}
                        {activeTicket?.status === 'SeasonTicket' && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeTicket !== undefined) {
                                buyTicketFree(activeTicket, 'Open')
                              }
                            }}
                          >
                            {t('release-ticket')}
                          </button>
                        )}
                        {activeTicket?.status === 'Open' && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeTicket !== undefined) {
                                buyTicketFree(activeTicket, 'Booked')
                              }
                            }}
                          >
                            {t('buy-ticket-free')}
                          </button>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <Transition.Root show={activeSeat !== undefined} as={Fragment}>
        <Dialog
          as="div"
          className={`${state.theme} relative z-10`}
          onClose={() => setActiveSeat(undefined)}
        >
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col divide-y divide-gray-800 bg-gray-800 shadow-xl">
                      <div className="h-0 flex-1 overflow-y-auto">
                        <div className="bg-gray-800 px-4 py-6 sm:px-6">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-base font-semibold leading-6 text-white">
                              {t('seat-information')}
                            </Dialog.Title>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="relative rounded-md bg-gray-700 text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                onClick={() => setActiveTicket(undefined)}
                              >
                                <span className="absolute -inset-2.5" />
                                <span className="sr-only">{t('close')}</span>
                                <XMarkIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                        {activeSeat !== undefined && (
                          <div className="flex flex-1 flex-col justify-between border-t border-gray-200 pt-2">
                            <div className="divide-y px-4 sm:px-6">
                              <p className="text-gray-200">
                                {t('status')}:{' '}
                                <span className="font-semibold">
                                  {activeSeat.special_seat_status}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('row')}:{' '}
                                <span className="font-semibold">
                                  {activeSeat.row}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('seat')}:{' '}
                                <span className="font-semibold">
                                  {activeSeat.seat}
                                </span>
                              </p>
                              <p className="text-gray-200">
                                {t('wheelchair-seat')}:{' '}
                                <span className="font-semibold">
                                  {activeSeat.handicap_seat
                                    ? t('yes')
                                    : t('no')}
                                </span>
                                <ArrowsRightLeftIcon
                                  onClick={() => {
                                    setHandicapForSeat(
                                      activeSeat,
                                      !activeSeat.handicap_seat
                                    )
                                  }}
                                  className={classNames(
                                    'text-brand inline-block h-5 w-7 cursor-pointer px-1'
                                  )}
                                  aria-hidden="true"
                                />
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 justify-end p-4">
                        {activeSeat?.special_seat_status === null && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeSeat !== undefined) {
                                changeSeatStatus(activeSeat, 'Blocked')
                              }
                            }}
                          >
                            {t('block-seat')}
                          </button>
                        )}
                        {activeSeat?.special_seat_status === 'Blocked' && (
                          <button
                            className="bg-brand ml-4 inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                            onClick={() => {
                              if (activeSeat !== undefined) {
                                changeSeatStatus(activeSeat, 'Open')
                              }
                            }}
                          >
                            {t('unblock-seat')}
                          </button>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {rows?.map((row, rdx) => (
        <div className="flex h-11 flex-row items-center" key={rdx}>
          <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
            {row.find((seat) => seat !== undefined)?.row}
          </p>
          {row?.map((column) => (
            <div key={`${column.row}-${column.seat}`}>
              <div
                className={classNames(
                  'm-px flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md bg-yellow-400 hover:bg-blue-700 hover:text-white'
                )}
                onClick={() => deleteSeat(column.pk, column.sk)}
              >
                <p className="text-center text-xs text-gray-800 dark:text-gray-100">
                  {column.handicap_seat === true ? (
                    <WheelChairIcon />
                  ) : (
                    <>{column.seat}</>
                  )}
                </p>
              </div>
              <div
                className={classNames(
                  '-ml-px -mt-2 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm bg-white'
                )}
              ></div>
            </div>
          ))}
          <p className="text-md ml-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
            {row.find((seat) => seat !== undefined)?.row}
          </p>
        </div>
      ))}
      <div>
        {mode === 'choose' && (
          <React.Fragment>
            {newRows?.map((row, rdx) => (
              <div className="flex h-11 flex-row items-center" key={rdx}>
                <div className="flex flex-1 flex-row items-center">
                  <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                    {row.find((seat) => seat.Seat !== undefined)?.Seat?.row}
                  </p>
                  {row?.map((column, cdx) => (
                    <React.Fragment key={`${row}-${cdx}`}>
                      {column.Seat && (
                        <div>
                          <div
                            data-seat-element="true"
                            data-row-index={rdx}
                            data-seat-index={cdx}
                            className={classNames(
                              column.Seat.special_seat_status === 'Blocked'
                                ? 'bg-gray-400'
                                : 'bg-gray-50',
                              selectedSeats.some(
                                (seat) =>
                                  seat.rowIndex === rdx &&
                                  seat.seatIndex === cdx
                              )
                                ? 'ring-2 ring-orange-500'
                                : '',
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md hover:bg-blue-700 hover:text-white'
                            )}
                            onClick={() => setActiveSeat(column.Seat)}
                            onMouseDown={() => handleSeatMouseDown(rdx, cdx)}
                            onMouseEnter={() => handleSeatMouseEnter(rdx, cdx)}
                          >
                            <p
                              className={classNames(
                                column.Seat.special_seat_status === 'Blocked'
                                  ? 'text-gray-100'
                                  : 'text-gray-800',
                                'text-center text-xs'
                              )}
                            >
                              {column?.Seat?.handicap_seat === true ? (
                                <WheelChairIcon />
                              ) : (
                                <>{column?.Seat?.seat}</>
                              )}
                            </p>
                          </div>
                          <div
                            className={classNames(
                              column.Seat.special_seat_status === 'Blocked'
                                ? 'border-t border-t-gray-50 bg-gray-400'
                                : 'border-t border-t-gray-400 bg-gray-50',
                              '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm'
                            )}
                          ></div>
                        </div>
                      )}
                      {column.Space && (
                        <div>
                          <div
                            className={classNames(
                              column?.Seat?.special_seat_status === 'Blocked'
                                ? 'bg-gray-400'
                                : 'bg-gray-50',
                              'm-px mx-1 h-8 w-6 cursor-pointer rounded-t-md hover:bg-blue-700 hover:text-white'
                            )}
                          ></div>
                        </div>
                      )}
                      {column.Stairs && (
                        <div>
                          <StairsIcon className="text-gray-800" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-md ml-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                  {row.find((seat) => seat.Seat !== undefined)?.Seat?.row}
                </p>
              </div>
            ))}
          </React.Fragment>
        )}
        {mode === 'delete-or-add' && (
          <React.Fragment>
            {newRows?.map((row, rdx) => (
              <div className="flex h-11 flex-row items-center" key={rdx}>
                <div className="flex flex-1 flex-row items-center">
                  <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                    {row.find((seat) => seat.Seat !== undefined)?.Seat?.row}
                  </p>
                  {row?.map((column, cdx) => (
                    <React.Fragment key={`${row}-${cdx}`}>
                      {column.Seat && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md bg-gray-50 hover:bg-blue-700 hover:text-white'
                            )}
                            onClick={() =>
                              deleteSeat(
                                column?.Seat?.pk ?? '',
                                column.Seat?.sk ?? ''
                              )
                            }
                          >
                            <p
                              className={classNames(
                                'text-center text-xs text-gray-800'
                              )}
                            >
                              {column?.Seat?.handicap_seat === true ? (
                                <WheelChairIcon />
                              ) : (
                                <>{column?.Seat?.seat}</>
                              )}
                            </p>
                          </div>
                          <div
                            className={classNames(
                              '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm border-t border-t-gray-400 bg-gray-50'
                            )}
                          ></div>
                        </div>
                      )}
                      {column.Space && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md border border-yellow-400 hover:border-gray-400'
                            )}
                            onClick={() => {
                              // Delete space
                              const localRow = [...row]
                              localRow.splice(cdx, 1)
                              const newRowsCopy = [...newRows]
                              newRowsCopy[rdx] = localRow
                              setNewRows(newRowsCopy)
                            }}
                          ></div>
                        </div>
                      )}
                      {column.Stairs && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md border border-blue-400 bg-gray-200 hover:border-gray-400'
                            )}
                            onClick={() => {
                              // Delete stairs
                              const localRow = [...row]
                              localRow.splice(cdx, 1)
                              const newRowsCopy = [...newRows]
                              newRowsCopy[rdx] = localRow
                              setNewRows(newRowsCopy)
                            }}
                          >
                            <p className="text-center text-xs text-gray-800">
                              ↕
                            </p>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div
                  className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                  onClick={() => {
                    const localRow = [...row]
                    localRow.push({
                      Space: true,
                    })
                    const newRowsCopy = [...newRows]
                    newRowsCopy[rdx] = localRow
                    setNewRows(newRowsCopy)
                  }}
                >
                  <SquaresPlusIcon className="h-6 w-6" title="Add Space" />
                </div>
                <div
                  className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                  onClick={() => {
                    const localRow = [...row]
                    localRow.push({
                      Stairs: true,
                    })
                    const newRowsCopy = [...newRows]
                    newRowsCopy[rdx] = localRow
                    setNewRows(newRowsCopy)
                  }}
                >
                  <ArrowDownIcon className="h-6 w-6" title="Add Stairs" />
                </div>
                <div
                  className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                  onClick={() => {
                    const newRowsCopy = [...newRows]
                    newRowsCopy[rdx] = row.reverse()
                    setNewRows(newRowsCopy)
                  }}
                >
                  <ArrowsRightLeftIcon className="h-6 w-6" />
                </div>
              </div>
            ))}
          </React.Fragment>
        )}
        {mode === 'drag-and-drop' && (
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={newRows?.map((_row, idx) => `${idx}`) ?? []}
              strategy={verticalListSortingStrategy}
            >
              {newRows?.map((row, index) => (
                <DraggableRow key={`${index}`} id={`${index}`}>
                  <Row
                    id={`row-${index}`}
                    row={index}
                    seats={row}
                    setRow={(rowIndex, row) => {
                      const newRowsCopy = [...newRows]
                      newRowsCopy[rowIndex] = row
                      setNewRows(newRowsCopy)
                    }}
                  />
                </DraggableRow>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
      <div>
        {mode === 'choose' && (
          <React.Fragment>
            {tickets?.map((row, rdx) => (
              <div className="flex h-11 flex-row items-center" key={rdx}>
                <div className="flex flex-1 flex-row items-center">
                  <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                    {row.find((seat) => seat.Ticket !== undefined)?.Ticket?.row}
                  </p>
                  {row?.map((column, cdx) => (
                    <React.Fragment key={`${row}-${cdx}`}>
                      {column.Ticket && (
                        <div>
                          <div
                            data-seat-element="true"
                            data-row-index={rdx}
                            data-seat-index={cdx}
                            className={classNames(
                              column.Ticket.status === 'Booked' ||
                                column.Ticket.status === 'Blocked'
                                ? 'bg-gray-700'
                                : column.Ticket.status === 'Reserved'
                                ? 'bg-gray-400'
                                : 'bg-gray-50',
                              selectedSeats.some(
                                (seat) =>
                                  seat.rowIndex === rdx &&
                                  seat.seatIndex === cdx
                              )
                                ? 'ring-2 ring-orange-500'
                                : '',
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md hover:bg-blue-700 hover:text-white'
                            )}
                            onClick={() => setActiveTicket(column.Ticket)}
                            onMouseDown={() => handleSeatMouseDown(rdx, cdx)}
                            onMouseEnter={() => handleSeatMouseEnter(rdx, cdx)}
                          >
                            <p
                              className={classNames(
                                column.Ticket.status === 'Booked'
                                  ? 'text-gray-100'
                                  : 'text-gray-800',
                                'text-center text-xs'
                              )}
                            >
                              {column?.Ticket?.handicap_seat === true ? (
                                <WheelChairIcon />
                              ) : (
                                <>{column?.Ticket?.seat}</>
                              )}
                            </p>
                          </div>
                          <div
                            className={classNames(
                              column.Ticket.status === 'Booked' ||
                                column.Ticket.status === 'Blocked'
                                ? 'border-t border-t-gray-50 bg-gray-700'
                                : column.Ticket.status === 'Reserved'
                                ? 'border-t border-t-gray-50 bg-gray-400'
                                : 'border-t border-t-gray-400 bg-gray-50',
                              '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm'
                            )}
                          ></div>
                        </div>
                      )}
                      {column.Space && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 h-8 w-6 cursor-pointer rounded-t-md'
                            )}
                          ></div>
                        </div>
                      )}
                      {column.Stairs && (
                        <div>
                          <StairsIcon className="text-gray-800" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-md ml-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                  {row.find((seat) => seat.Ticket !== undefined)?.Ticket?.row}
                </p>
              </div>
            ))}
          </React.Fragment>
        )}
        {mode === 'delete-or-add' && (
          <React.Fragment>
            {tickets?.map((row, rdx) => (
              <div className="flex h-11 flex-row items-center" key={rdx}>
                <div className="flex flex-1 flex-row items-center">
                  <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                    {row.find((seat) => seat.Ticket !== undefined)?.Ticket?.row}
                  </p>
                  {row?.map((column, cdx) => (
                    <React.Fragment key={`${row}-${cdx}`}>
                      {column.Ticket && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md bg-gray-50 hover:bg-blue-700 hover:text-white'
                            )}
                            onClick={() =>
                              deleteSeat(
                                column?.Ticket?.pk ?? '',
                                column.Ticket?.sk ?? ''
                              )
                            }
                          >
                            <p
                              className={classNames(
                                'text-center text-xs text-gray-800'
                              )}
                            >
                              {column?.Ticket?.handicap_seat === true ? (
                                <WheelChairIcon />
                              ) : (
                                <>{column?.Ticket?.seat}</>
                              )}
                            </p>
                          </div>
                          <div
                            className={classNames(
                              '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm bg-gray-50'
                            )}
                          ></div>
                        </div>
                      )}
                      {column.Space && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md border border-yellow-400 hover:border-gray-400'
                            )}
                            onClick={() => {
                              // Delete space
                              const localRow = [...row]
                              localRow.splice(cdx, 1)
                              const newRowsCopy = [...tickets]
                              newRowsCopy[rdx] = localRow
                              setTickets(newRowsCopy)
                            }}
                          ></div>
                        </div>
                      )}
                      {column.Stairs && (
                        <div>
                          <div
                            className={classNames(
                              'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md border border-blue-400 bg-gray-200 hover:border-gray-400'
                            )}
                            onClick={() => {
                              // Delete stairs
                              const localRow = [...row]
                              localRow.splice(cdx, 1)
                              const newRowsCopy = [...tickets]
                              newRowsCopy[rdx] = localRow
                              setTickets(newRowsCopy)
                            }}
                          >
                            <p className="text-center text-xs text-gray-800">
                              ↕
                            </p>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div
                  className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                  onClick={() => {
                    const localRow = [...row]
                    localRow.push({
                      Space: true,
                    })
                    const newRowsCopy = [...tickets]
                    newRowsCopy[rdx] = localRow
                    setTickets(newRowsCopy)
                  }}
                >
                  <SquaresPlusIcon className="h-6 w-6" title="Add Space" />
                </div>
                <div
                  className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                  onClick={() => {
                    const localRow = [...row]
                    localRow.push({
                      Stairs: true,
                    })
                    const newRowsCopy = [...tickets]
                    newRowsCopy[rdx] = localRow
                    setTickets(newRowsCopy)
                  }}
                >
                  <ArrowDownIcon className="h-6 w-6" title="Add Stairs" />
                </div>
                <div
                  className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                  onClick={() => {
                    const newRowsCopy = [...tickets]
                    newRowsCopy[rdx] = row.reverse()
                    setTickets(newRowsCopy)
                  }}
                >
                  <ArrowsRightLeftIcon className="h-6 w-6" />
                </div>
              </div>
            ))}
          </React.Fragment>
        )}
        {mode === 'drag-and-drop' && (
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={tickets?.map((_row, idx) => `${idx}`) ?? []}
              strategy={verticalListSortingStrategy}
            >
              {tickets?.map((row, index) => (
                <DraggableRow key={`${index}`} id={`${index}`}>
                  <Row
                    id={`row-${index}`}
                    row={index}
                    seats={row}
                    setRow={(rowIndex, row) => {
                      const ticketsCopy = [...tickets]
                      ticketsCopy[rowIndex] = row
                      setTickets(ticketsCopy)
                    }}
                  />
                </DraggableRow>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
      <div className="mt-4 flex flex-row items-center justify-start gap-x-4">
        <div className="flex items-center">
          <div>
            <div
              className={
                'm-px mx-1 flex h-8 w-6 items-center justify-center rounded-t-md bg-gray-50'
              }
            ></div>
            <div
              className={
                '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-t-sm border-t border-t-gray-400 bg-gray-50'
              }
            ></div>
          </div>
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {t('seat-available')}
          </span>
        </div>
        <div className="flex items-center">
          <div>
            <div
              className={
                'm-px mx-1 flex h-8 w-6 items-center justify-center rounded-t-md bg-gray-400'
              }
            ></div>
            <div
              className={
                '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-t-sm border-t border-t-gray-50 bg-gray-400'
              }
            ></div>
          </div>
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {t('seat-reserved')}
          </span>
        </div>
        <div className="flex items-center">
          <div>
            <div
              className={
                'm-px mx-1 flex h-8 w-6 items-center justify-center rounded-t-md bg-gray-700'
              }
            ></div>
            <div
              className={
                '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-t-sm border-t border-t-gray-50 bg-gray-700'
              }
            ></div>
          </div>
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {t('seat-booked')}
          </span>
        </div>
        <div className="flex items-center">
          <p className={'text-center text-xs text-gray-800 dark:text-gray-200'}>
            <WheelChairIcon />
          </p>
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {t('seat-wheelchair')}
          </span>
        </div>
      </div>
    </div>
  )
}

interface SeatMakerProps {
  aid?: string | string[]
  sid?: string | string[]
  section: ArenaSection | undefined
  eventSk?: string | string[]
  seasonTicketSk?: string | string[]
  existingPrices?: Price[]
}

type Modes = 'choose' | 'drag-and-drop' | 'delete-or-add'

export const SeatMaker = ({
  aid,
  sid,
  section,
  eventSk,
  seasonTicketSk,
  existingPrices,
}: SeatMakerProps) => {
  const { t } = useTranslation('common')
  const [rows, setRows] = useState<ArenaSectionSeat[][]>([])
  const [newRows, setNewRows] = useState<ArenaSectionSeatTypes[][]>([])
  const [tickets, setTickets] = useState<ArenaSectionSeatTypes[][]>([])
  const [rawTickets, setRawTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [showFailureMessage, setShowFailureMessage] = useState<boolean>(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)
  const [mode, setMode] = useState<Modes>('choose')

  // State for click-and-drag selection
  const [selectedSeats, setSelectedSeats] = useState<
    { rowIndex: number; seatIndex: number }[]
  >([])
  const [isSelecting, setIsSelecting] = useState<boolean>(false)
  const [showPriceModal, setShowPriceModal] = useState<boolean>(false)
  const [modalPrices, setModalPrices] = useState<Price[]>(
    existingPrices ?? [
      {
        name: 'Standard',
        value: 0,
        value_in_cents: 0,
        requires_verification: null,
      },
    ]
  )

  const [row, setRow] = useState<string>('')
  const [seats, setSeats] = useState<string>('')
  const [firstSeat, setFirstSeat] = useState<string>('')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      if (section !== undefined) {
        if (eventSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${eventSk}/${section.uuid}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          let res: StandardAPIRequest<ArenaSectionSeatTypes[][]> =
            await response.json()
          if (res.error) {
            throw new Error(res.error.message)
          }
          setRawTickets(
            res.data
              ?.flat()
              ?.map((seat) => seat.Ticket)
              .filter((ticket): ticket is Ticket => ticket !== undefined) ?? []
          )
          setTickets(res.data ?? [])
        } else if (seasonTicketSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/ticket/season_ticket/${seasonTicketSk}/${section.uuid}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          let res: StandardAPIRequest<ArenaSectionSeatTypes[][]> =
            await response.json()
          if (res.error) {
            throw new Error(res.error.message)
          }
          setRawTickets(
            res.data
              ?.flat()
              ?.map((seat) => seat.Ticket)
              .filter((ticket): ticket is Ticket => ticket !== undefined) ?? []
          )
          setTickets(res.data ?? [])
        } else {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${section.arena}/section/${section.uuid}/seat/ordered`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          let res: StandardAPIRequest<ArenaSectionSeatTypes[][]> =
            await response.json()
          if (res.error) {
            throw new Error(res.error.message)
          }
          setNewRows(res.data ?? [])
        }
      }
    } catch (error: any) {
      captureException(error)
    } finally {
      setLoading(false)
    }
  }, [section, eventSk, seasonTicketSk])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Reset selection when clicking outside or on escape key
  const handleDocumentMouseUp = useCallback(() => {
    if (isSelecting) {
      setIsSelecting(false)
    }
  }, [isSelecting])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsSelecting(false)
      setSelectedSeats([])
    }
  }, [])

  useEffect(() => {
    // Add document-level event handlers
    document.addEventListener('mouseup', handleDocumentMouseUp)
    document.addEventListener('keydown', handleKeyDown)

    // Disable text selection during selection mode
    if (isSelecting) {
      document.body.style.userSelect = 'none'
    } else {
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.userSelect = ''
    }
  }, [handleDocumentMouseUp, handleKeyDown, isSelecting])

  // Function to update prices for selected seats
  const updateSelectedSeatsPrices = async () => {
    if (selectedSeats.length === 0 || modalPrices.length === 0) return

    try {
      setLoading(true)

      // Process each selected seat
      for (const { rowIndex, seatIndex } of selectedSeats) {
        const currentArray = eventSk || seasonTicketSk ? tickets : newRows
        const seat = currentArray[rowIndex]?.[seatIndex]

        if (seat && (seat.Seat || seat.Ticket)) {
          const seatId = seat.Seat?.sk || seat.Ticket?.sk

          if (seatId) {
            // API endpoint depends on if it's a ticket or regular seat
            let endpoint
            if (eventSk) {
              endpoint = `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${eventSk}/${section?.uuid}/ticket/${seatId}/ticket`
            } else if (seasonTicketSk) {
              endpoint = `${process.env.NEXT_PUBLIC_API_URL}/ticket/season_ticket/${seasonTicketSk}/${section?.uuid}/ticket`
            } else {
              endpoint = `${process.env.NEXT_PUBLIC_API_URL}/arena/${aid}/section/${sid}/seat/${seatId}`
            }

            await fetch(endpoint, {
              method: 'POST',
              body: JSON.stringify({
                ticket_id: seatId,
                price: modalPrices,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            })
          }
        }
      }

      setShowSuccessMessage(true)
      setShowPriceModal(false)
      // Refresh data to show updated prices
      await fetchData()
    } catch (error) {
      captureException(error)
      setShowFailureMessage(true)
    } finally {
      setLoading(false)
      setSelectedSeats([])
      // Reset the modal prices to default
      setModalPrices([
        {
          name: 'Standard',
          value: 0,
          value_in_cents: 0,
          requires_verification: null,
        },
      ])
    }
  }

  const addSeats = async (row: number, seats: number, first_seat: number) => {
    try {
      setLoading(true)
      if (section !== undefined) {
        if (eventSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${eventSk}/${section.uuid}`,
            {
              method: 'PUT',
              body: JSON.stringify({
                row,
                seats,
                first_seat,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          fetchData()
          setShowSuccessMessage(true)
        } else if (seasonTicketSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/ticket/season_ticket/${seasonTicketSk}/${section.uuid}`,
            {
              method: 'PUT',
              body: JSON.stringify({
                row,
                seats,
                first_seat,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          fetchData()
          setShowSuccessMessage(true)
        } else {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${section.arena}/section/${section.uuid}/seat`,
            {
              method: 'PUT',
              body: JSON.stringify({
                row,
                seats,
                first_seat,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          fetchData()
          setShowSuccessMessage(true)
        }
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      setLoading(false)
    }
  }

  const deleteSeat = async (pk: string, sk: string) => {
    try {
      setLoading(true)
      if (section !== undefined) {
        if (eventSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/event/${eventSk}/${section.uuid}/seat`,
            {
              method: 'DELETE',
              body: JSON.stringify({
                pk,
                sk,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          fetchData()
          setShowSuccessMessage(true)
        } else if (seasonTicketSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/event/${seasonTicketSk}/${section.uuid}/seat`,
            {
              method: 'DELETE',
              body: JSON.stringify({
                pk,
                sk,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          fetchData()
          setShowSuccessMessage(true)
        } else {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${section.arena}/section/${section.uuid}/seat`,
            {
              method: 'DELETE',
              body: JSON.stringify({
                pk,
                sk,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          fetchData()
          setShowSuccessMessage(true)
        }
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      setLoading(false)
    }
  }

  const createNewRows = async () => {
    try {
      const localSeats = parseInt(seats, 10)
      const localRow = parseInt(row, 10)
      const localFirstSeat = parseInt(firstSeat, 10)
      if (isNaN(localSeats) || isNaN(localRow) || isNaN(localFirstSeat)) {
        return
      }
      if (localSeats < 1 || localRow < 1 || localFirstSeat < 1) {
        return
      }
      await addSeats(localRow, localSeats, localFirstSeat)
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    }
  }

  const buyTicketFree = async (ticket: Ticket, status: string) => {
    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${ticket.event}/${ticket.section}/ticket`,
        {
          method: 'POST',
          body: JSON.stringify({
            ticket_id: ticket.sk,
            status,
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await clientGetIdToken()}`,
          },
        }
      )
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      fetchData()
    }
  }

  const setHandicap = async (ticket: Ticket, handicap: boolean) => {
    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${ticket.event}/${ticket.section}/ticket`,
        {
          method: 'POST',
          body: JSON.stringify({
            ticket_id: ticket.sk,
            handicap,
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await clientGetIdToken()}`,
          },
        }
      )
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      fetchData()
    }
  }

  const changeSeatStatus = async (
    seat: ArenaSectionSeat,
    new_status: string | null
  ) => {
    if (aid === undefined || sid === undefined) {
      return
    }
    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/arena/${aid}/section/${sid}/seat`,
        {
          method: 'POST',
          body: JSON.stringify({
            pk: seat.pk,
            sk: seat.sk,
            new_status,
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await clientGetIdToken()}`,
          },
        }
      )
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      fetchData()
    }
  }

  const setHandicapForSeat = async (
    seat: ArenaSectionSeat,
    handicap_seat: boolean
  ) => {
    if (aid === undefined || sid === undefined) {
      return
    }
    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/arena/${aid}/section/${sid}/seat`,
        {
          method: 'POST',
          body: JSON.stringify({
            pk: seat.pk,
            sk: seat.sk,
            handicap_seat,
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await clientGetIdToken()}`,
          },
        }
      )
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      fetchData()
    }
  }

  async function saveCurrentOrder() {
    try {
      if (newRows.length > 0) {
        let ordering: ArenaSectionOrderingTypes[][] = newRows?.map((row) =>
          row?.map((seat) => {
            if (seat.Seat) {
              return { Seat: seat.Seat.sk }
            } else if (seat.Space) {
              return { Space: true }
            } else if (seat.Stairs) {
              return { Stairs: true }
            } else if (seat.Ticket) {
              return { Seat: seat.Ticket.sk }
            } else {
              return { Space: true }
            }
          })
        )

        if (eventSk) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/event/${eventSk}/${section?.uuid}/ordering`,
            {
              method: 'POST',
              body: JSON.stringify({
                ordering,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          setShowSuccessMessage(true)
        } else if (seasonTicketSk) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/season_ticket/${seasonTicketSk}/${section?.uuid}/ordering`,
            {
              method: 'POST',
              body: JSON.stringify({
                ordering,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          setShowSuccessMessage(true)
        } else {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${section?.arena}/section/${section?.uuid}/ordering`,
            {
              method: 'POST',
              body: JSON.stringify({
                ordering,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
        }

        setShowSuccessMessage(true)
      }
      if (tickets.length > 0) {
        let ordering: ArenaSectionOrderingTypes[][] = tickets?.map((row) =>
          row?.map((seat) => {
            if (seat.Seat) {
              return { Seat: seat.Seat.sk }
            } else if (seat.Space) {
              return { Space: true }
            } else if (seat.Stairs) {
              return { Stairs: true }
            } else if (seat.Ticket) {
              return { Seat: seat.Ticket.sk }
            } else {
              return { Space: true }
            }
          })
        )

        if (eventSk) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/event/${eventSk}/${section?.uuid}/ordering`,
            {
              method: 'POST',
              body: JSON.stringify({
                ordering,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          setShowSuccessMessage(true)
        } else if (seasonTicketSk) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/season_ticket/${seasonTicketSk}/${section?.uuid}/ordering`,
            {
              method: 'POST',
              body: JSON.stringify({
                ordering,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          setShowSuccessMessage(true)
        } else {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${section?.arena}/section/${section?.uuid}/ordering`,
            {
              method: 'POST',
              body: JSON.stringify({
                ordering,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
        }

        setShowSuccessMessage(true)
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    }
  }

  const columns = [
    {
      key: 'number' as const,
      header: t('seat-maker-column-number'),
      sortable: true,
    },
    {
      key: 'section_name' as const,
      header: t('seat-maker-column-section'),
      sortable: true,
    },
    {
      key: 'row' as const,
      header: t('seat-maker-column-row'),
      sortable: true,
    },
    {
      key: 'seat' as const,
      header: t('seat-maker-column-seat'),
      sortable: true,
    },
    {
      key: 'status' as const,
      header: t('seat-maker-column-status'),
      sortable: true,
    },
    {
      key: 'actions' as const,
      header: t('seat-maker-column-actions'),
      sortable: false,
      render: (row: Ticket) => (
        <div className="flex items-center justify-end">
          <button
            className="inline-flex items-center rounded-lg p-0.5 text-center text-sm font-medium text-gray-500 hover:text-gray-800 focus:outline-none dark:text-gray-400 dark:hover:text-gray-100"
            onClick={() =>
              buyTicketFree(row, row.status === 'Open' ? 'Booked' : 'Open')
            }
          >
            {row.status === 'Open'
              ? t('seat-maker-buy-ticket-free')
              : t('seat-maker-release-ticket')}
          </button>
          {row.status === 'Booked' && (
            <>
              <span className="mx-1 text-white">|</span>
              <a
                href={`https://api.liveday.se/ticket/ticket/${row.ticket_key}/free.pdf`}
                className="inline-flex items-center rounded-lg p-0.5 text-center text-sm font-medium text-gray-500 hover:text-gray-800 focus:outline-none dark:text-gray-400 dark:hover:text-gray-100"
              >
                {t('seat-maker-ticket-pdf')}
              </a>
              <span className="mx-1 text-white">|</span>
              <button
                className="inline-flex items-center rounded-lg p-0.5 text-center text-sm font-medium text-gray-500 hover:text-gray-800 focus:outline-none dark:text-gray-400 dark:hover:text-gray-100"
                onClick={() => exportQRCode(row.ticket_key)}
              >
                {t('seat-maker-download-qr-code')}
              </button>
            </>
          )}
        </div>
      ),
    },
  ]

  return (
    <React.Fragment>
      <FailureNotification
        shouldShow={showFailureMessage}
        message={t('unable-to-add-seat')}
        subtitle={t('unable-to-add-seat-try-again-later')}
        hide={() => setShowFailureMessage(false)}
      />

      {/* Price Update Modal */}
      <Transition.Root show={showPriceModal} as={Fragment}>
        <Dialog
          as="div"
          className={`relative z-10`}
          onClose={() => setShowPriceModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all dark:bg-gray-900 sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
                      <svg
                        className="h-6 w-6 text-orange-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                      >
                        {t('update-price-for-selected-seats')}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {t('selected-seats')}: {selectedSeats.length}
                        </p>
                        <div className="mt-4">
                          <Pricing
                            price={modalPrices}
                            setPrice={setModalPrices}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                      onClick={updateSelectedSeatsPrices}
                    >
                      {loading ? <ButtonLoader /> : t('update-price')}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                      onClick={() => {
                        setShowPriceModal(false)
                        setSelectedSeats([])
                      }}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <SuccessNotification
        shouldShow={showSuccessMessage}
        message={t('seat-added')}
        subtitle={t('we-added-the-seat')}
        hide={() => setShowSuccessMessage(false)}
      />
      {loading ? (
        <ButtonLoader />
      ) : (
        <React.Fragment>
          {section?.seated_section ? (
            <section
              aria-labelledby="product-heading"
              className="mt-6 lg:col-span-2 lg:mt-0 xl:col-span-3"
            >
              {mode === 'delete-or-add' && (
                <div className="mb-4 rounded-lg bg-gray-100 p-4 dark:bg-gray-800">
                  <p className="pt-4 text-gray-900 dark:text-white">
                    {t('seat-maker-add-entire-row')}
                  </p>
                  <div className="pt-2">
                    <label
                      htmlFor="id"
                      className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                    >
                      {t('seat-maker-row-number')}
                    </label>
                    <input
                      value={row}
                      onChange={(e) => setRow(e.target.value)}
                      type="text"
                      name="row"
                      id="row"
                      className="focus:ring-primary-600 focus:border-primary-600 dark:focus:ring-primary-500 dark:focus:border-primary-500 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                      placeholder={t('seat-maker-row-number-placeholder') ?? ''}
                    />
                  </div>
                  <div className="pt-2 lg:grid lg:grid-cols-4 lg:gap-x-4">
                    <div className="col-span-2">
                      <label
                        htmlFor="seats"
                        className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                      >
                        {t('seat-maker-number-of-seats')}
                      </label>
                      <input
                        value={seats}
                        onChange={(e) => setSeats(e.target.value)}
                        type="text"
                        name="seats"
                        id="seats"
                        className="focus:ring-primary-600 focus:border-primary-600 dark:focus:ring-primary-500 dark:focus:border-primary-500 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                        placeholder={
                          t('seat-maker-number-of-seats-placeholder') ?? ''
                        }
                      />
                    </div>
                    <div className="col-span-2">
                      <label
                        htmlFor="first_seat"
                        className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                      >
                        {t('seat-maker-first-seat-number')}
                      </label>
                      <input
                        value={firstSeat}
                        onChange={(e) => setFirstSeat(e.target.value)}
                        type="text"
                        name="first_seat"
                        id="first_seat"
                        className="focus:ring-primary-600 focus:border-primary-600 dark:focus:ring-primary-500 dark:focus:border-primary-500 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                        placeholder={
                          t('seat-maker-first-seat-number-placeholder') ?? ''
                        }
                      />
                    </div>
                  </div>
                  <button
                    className="mt-2 w-full rounded bg-orange-500 px-4 py-2 text-white"
                    onClick={createNewRows}
                  >
                    {t('seat-maker-add-row')}
                  </button>
                </div>
              )}
              <div className="rounded-lg bg-gray-100 dark:bg-gray-800">
                <div className="flex flex-1 flex-grow flex-row items-center justify-between border-b border-gray-600 px-3 pb-4 pt-3">
                  <div className="flex flex-1 flex-grow">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {t('seat-maker-section')} {section?.name}
                    </h2>
                    {section.arrow_to_middle_angle && (
                      <div className="p-2">
                        <ArrowDownIcon
                          className="ml-4 h-4 w-4 text-gray-900 dark:text-white"
                          style={{
                            transform: `rotate(${section.arrow_to_middle_angle}deg)`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-shrink flex-row items-center">
                    {mode !== 'choose' && (
                      <React.Fragment>
                        <button
                          className="mr-4 mt-2 rounded-full bg-orange-500 px-4 py-2 text-white"
                          onClick={saveCurrentOrder}
                        >
                          {t('seat-maker-save')}
                        </button>
                        <div
                          className="text-md mr-2 cursor-pointer text-gray-900 hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                          onClick={() => {
                            setNewRows([...newRows].reverse())
                            setTickets([...tickets].reverse())
                          }}
                        >
                          <ArrowsUpDownIcon className="h-6 w-6" />
                        </div>
                      </React.Fragment>
                    )}
                    <div>
                      <RadioGroup
                        value={mode}
                        onChange={setMode}
                        className="mt-2"
                      >
                        <RadioGroup.Label className="sr-only">
                          {t('seat-maker-choose-mode')}
                        </RadioGroup.Label>
                        <div className="flex flex-row gap-x-3">
                          <RadioGroup.Option
                            value={'choose'}
                            className={({ active, checked }) =>
                              classNames(
                                active
                                  ? 'ring-brand-600 ring-2 ring-offset-2'
                                  : '',
                                checked
                                  ? 'focus:outline-none, bg-brand-600 hover:bg-brand-500 cursor-pointer text-white'
                                  : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                                'flex items-center justify-center rounded-md px-3 py-3 text-sm font-semibold uppercase sm:flex-1'
                              )
                            }
                          >
                            <RadioGroup.Label as="span">
                              {eventSk || seasonTicketSk
                                ? t('seat-maker-tickets')
                                : t('seat-maker-seats')}
                            </RadioGroup.Label>
                          </RadioGroup.Option>
                          <RadioGroup.Option
                            value={'drag-and-drop'}
                            className={({ active, checked }) =>
                              classNames(
                                active
                                  ? 'ring-brand-600 ring-2 ring-offset-2'
                                  : '',
                                checked
                                  ? 'focus:outline-none, bg-brand-600 hover:bg-brand-500 cursor-pointer text-white'
                                  : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                                'flex items-center justify-center rounded-md px-3 py-3 text-sm font-semibold uppercase sm:flex-1'
                              )
                            }
                          >
                            <RadioGroup.Label as="span">
                              {t('seat-maker-drag-and-drop')}
                            </RadioGroup.Label>
                          </RadioGroup.Option>
                          <RadioGroup.Option
                            value={'delete-or-add'}
                            className={({ active, checked }) =>
                              classNames(
                                active
                                  ? 'ring-brand-600 ring-2 ring-offset-2'
                                  : '',
                                checked
                                  ? 'focus:outline-none, bg-brand-600 hover:bg-brand-500 cursor-pointer text-white'
                                  : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                                'flex items-center justify-center rounded-md px-3 py-3 text-sm font-semibold uppercase sm:flex-1'
                              )
                            }
                          >
                            <RadioGroup.Label as="span">
                              {t('seat-maker-add-or-remove')}
                            </RadioGroup.Label>
                          </RadioGroup.Option>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <SeatGrid
                    eventSk={eventSk}
                    seasonTicketSk={seasonTicketSk}
                    rows={rows}
                    setRows={setRows}
                    newRows={newRows}
                    setNewRows={setNewRows}
                    tickets={tickets}
                    setTickets={setTickets}
                    deleteSeat={deleteSeat}
                    buyTicketFree={buyTicketFree}
                    mode={mode}
                    setHandicap={setHandicap}
                    changeSeatStatus={changeSeatStatus}
                    setHandicapForSeat={setHandicapForSeat}
                    selectedSeats={selectedSeats}
                    setSelectedSeats={setSelectedSeats}
                    isSelecting={isSelecting}
                    setIsSelecting={setIsSelecting}
                    onSelectionComplete={() => setShowPriceModal(true)}
                  />
                </div>
              </div>
            </section>
          ) : (
            <section className="relative flex-1">
              <GenericTable
                title={t('seat-maker-tickets') ?? ''}
                columns={columns}
                rowKeyField="sk"
                data={rawTickets
                  .sort((a, b) => a.sk.localeCompare(b.sk))
                  .map((ticket, index) => ({
                    ...ticket,
                    number: index + 1,
                  }))}
                data_length={rawTickets.length}
                initialSortColumn="sk"
                initialSortDirection="asc"
                emptyState={{
                  title: t('no-tickets') ?? '',
                  description: t('no-tickets-description') ?? '',
                  actionLabel: t('add-ticket') ?? '',
                  actionLink: '#',
                }}
                skipBasePadding
              />
            </section>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

interface SectionProps {
  section?: ArenaSection
  eventSk?: string | string[]
  seasonTicketSk?: string | string[]
  arena?: Arena
  refetchSection?: () => Promise<void>
}

export const Section = ({
  section,
  eventSk,
  seasonTicketSk,
  arena,
  refetchSection,
}: SectionProps) => {
  const { state } = useContext(AuthContext)
  const { t } = useTranslation('common')
  const router = useRouter()
  const [angle, setAngle] = useState<number>(
    (section?.arrow_to_middle_angle ?? 0) / 3.6
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [unseatedSeatsLoading, setUnseatedSeatsLoading] =
    useState<boolean>(false)
  const [showFailureMessage, setShowFailureMessage] = useState<boolean>(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)

  const [name, setName] = useState<string>(section?.name ?? '')
  const [nameErrorMessage, setNameErrorMessage] = useState<string>('')
  const validName = (localName: string): boolean => localName.length > 0
  const updateName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }
  const checkName = () => setNameErrorMessage(validName(name) ? '' : '')

  const [entrance, setEntrance] = useState<string>(section?.entrance ?? '')
  const [entranceErrorMessage, setEntranceErrorMessage] = useState<string>('')
  const validEntrance = (localEntrance: string): boolean =>
    localEntrance.length > 0
  const updateEntrance = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEntrance(event.target.value)
  }
  const checkEntrance = () =>
    setEntranceErrorMessage(validEntrance(entrance) ? '' : '')

  const [showAddUnseatedSeats, setShowAddUnseatedSeats] =
    useState<boolean>(false)
  const [svgIdModal, setSvgIdModal] = useState<boolean>(false)
  const [svgId, setSvgId] = useState<string>(section?.svg_id ?? '')
  const [svgIdErrorMessage, setSvgIdErrorMessage] = useState<string>('')
  const validSvgId = (localSvgId: string): boolean => localSvgId.length > 0
  const updateSvgId = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSvgId(event.target.value)
  }
  const checkSvgId = () => setSvgIdErrorMessage(validSvgId(svgId) ? '' : '')

  useEffect(() => {
    if (svgId.length > 0) {
      setSvgIdModal(false)
    }
  }, [svgId])

  const [unseatedSeats, setUnseatedSeats] = useState<string>(
    section?.unseated_seats?.toString() ?? ''
  )
  const [unseatedSeatsErrorMessage, setUnseatedSeatsErrorMessage] =
    useState<string>('')
  const validUnseatedSeats = (localUnseatedSeats: string): boolean =>
    localUnseatedSeats.length > 0
  const updateUnseatedSeats = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUnseatedSeats(event.target.value)
  }
  const checkUnseatedSeats = () =>
    setUnseatedSeatsErrorMessage(validUnseatedSeats(unseatedSeats) ? '' : '')

  const [price, setPrice] = useState<Price[]>(
    section?.price ?? [
      { name: 'Standard', value: 0, requires_verification: null },
    ]
  )

  const [seatedSection, setSeatedSection] = useState<boolean>(
    section?.seated_section ?? false
  )

  const [clickable, setClickable] = useState<boolean>(
    section?.clickable ?? false
  )

  const [showDeleteSection, setShowDeleteSection] = useState<boolean>(false)

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      setLoading(true)
      if (eventSk) {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/arena/event/${eventSk}/${section?.uuid}`,
          {
            method: 'POST',
            body: JSON.stringify({
              name: name.length > 0 ? name : null,
              entrance: entrance.length > 0 ? entrance : null,
              svg_id: svgId.length > 0 ? svgId : null,
              price: price.length > 0 ? price : null,
              unseated_seats:
                unseatedSeats.length > 0 ? parseInt(unseatedSeats, 10) : null,
              seated_section: seatedSection,
              clickable,
              blocked_section: !clickable,
              arrow_to_middle_angle: Math.round(angle * 3.6),
            }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        await response.json()
        setShowSuccessMessage(true)
      } else if (seasonTicketSk) {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/arena/season_ticket/${seasonTicketSk}/${section?.uuid}`,
          {
            method: 'POST',
            body: JSON.stringify({
              name: name.length > 0 ? name : null,
              entrance: entrance.length > 0 ? entrance : null,
              svg_id: svgId.length > 0 ? svgId : null,
              price: price.length > 0 ? price : null,
              seated_section: seatedSection,
              unseated_seats:
                unseatedSeats.length > 0 ? parseInt(unseatedSeats, 10) : null,
              clickable,
              blocked_section: !clickable,
              arrow_to_middle_angle: Math.round(angle * 3.6),
            }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        await response.json()
        setShowSuccessMessage(true)
      } else {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/arena/${section?.arena}/section/${section?.uuid}`,
          {
            method: 'POST',
            body: JSON.stringify({
              name: name.length > 0 ? name : null,
              entrance: entrance.length > 0 ? entrance : null,
              svg_id: svgId.length > 0 ? svgId : null,
              price: price.length > 0 ? price : null,
              seated_section: seatedSection,
              unseated_seats:
                unseatedSeats.length > 0 ? parseInt(unseatedSeats, 10) : null,
              clickable,
              blocked_section: !clickable,
              arrow_to_middle_angle: Math.round(angle * 3.6),
            }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        await response.json()
        setShowSuccessMessage(true)
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      setLoading(false)
      if (refetchSection) {
        await refetchSection()
      }
    }
  }

  const deleteSection = async () => {
    try {
      setLoading(true)
      if (eventSk) {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/arena/event/${eventSk}/${section?.uuid}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        await response.json()
        router.push(`/venue/events/${eventSk}`)
      } else if (seasonTicketSk) {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/arena/season_ticket/${seasonTicketSk}/${section?.uuid}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        await response.json()
        router.push(`/venue/season_tickets/${seasonTicketSk}`)
      } else {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/arena/${section?.arena}/section/${section?.uuid}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        await response.json()
        router.push(`/venue/arenas/${section?.arena}`)
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      setLoading(false)
    }
  }

  const addUnseatedSeats = async (unnumbered_tickets: number) => {
    try {
      setUnseatedSeatsLoading(true)
      if (section !== undefined) {
        if (eventSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${eventSk}/${section.uuid}`,
            {
              method: 'PUT',
              body: JSON.stringify({
                unnumbered_tickets,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          setShowSuccessMessage(true)
          setShowAddUnseatedSeats(false)
        } else if (seasonTicketSk !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/ticket/season_ticket/${seasonTicketSk}/${section.uuid}`,
            {
              method: 'PUT',
              body: JSON.stringify({
                unnumbered_tickets,
              }),
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          await response.json()
          setShowSuccessMessage(true)
          setShowAddUnseatedSeats(false)
        }
      }
    } catch (err) {
      captureException(err)
      setShowFailureMessage(true)
    } finally {
      setUnseatedSeatsLoading(false)
      setShowAddUnseatedSeats(false)
      if (refetchSection) {
        await refetchSection()
      }
    }
  }

  return (
    <div className="flex-1 xl:overflow-y-auto">
      <>
        <Transition.Root show={svgIdModal} as={Fragment}>
          <Dialog
            as="div"
            className={`${state.theme} relative z-10`}
            onClose={() => setSvgIdModal(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 hidden bg-gray-500 bg-opacity-75 transition-opacity md:block" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4">
                {/* This element is to trick the browser into centering the modal contents. */}
                <span
                  className="hidden md:inline-block md:h-screen md:align-middle"
                  aria-hidden="true"
                >
                  &#8203;
                </span>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                  enterTo="opacity-100 translate-y-0 md:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 md:scale-100"
                  leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                >
                  <Dialog.Panel className="flex w-full max-w-5xl text-left text-base transition">
                    <div className="relative flex w-full items-center overflow-hidden rounded-md bg-white  shadow-2xl">
                      <button
                        type="button"
                        className="absolute right-2 top-2 text-gray-400 hover:text-gray-500"
                        onClick={() => setSvgIdModal(false)}
                      >
                        <span className="sr-only">{t('close')}</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>

                      <div className="flex flex-col p-8">
                        {arena?.svg_name && (
                          <OrganizerArena
                            url={arena?.svg_name ?? ''}
                            setActive={setSvgId}
                          />
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </>
      <Transition.Root show={showAddUnseatedSeats} as={Fragment}>
        <Dialog
          as="div"
          className={`${state.theme} relative z-10`}
          onClose={() => setShowAddUnseatedSeats(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 hidden bg-gray-500 bg-opacity-75 transition-opacity md:block" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4">
              {/* This element is to trick the browser into centering the modal contents. */}
              <span
                className="hidden md:inline-block md:h-screen md:align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                enterTo="opacity-100 translate-y-0 md:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 md:scale-100"
                leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
              >
                <Dialog.Panel className="flex w-full max-w-5xl text-left text-base transition">
                  <div className="relative flex w-full items-center overflow-hidden rounded-md bg-white  shadow-2xl">
                    <button
                      type="button"
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-500"
                      onClick={() => setShowAddUnseatedSeats(false)}
                    >
                      <span className="sr-only">{t('close')}</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>

                    <div className="flex flex-1 flex-col p-8">
                      <label
                        htmlFor="unseated-seats"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t('unseated-seats')}
                      </label>
                      <input
                        type="text"
                        name="unseated-seats"
                        id="unseated-seats"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                        value={unseatedSeats}
                        onChange={updateUnseatedSeats}
                        onBlur={checkUnseatedSeats}
                      />
                      {unseatedSeatsErrorMessage.length > 0 && (
                        <p className="mt-1 text-red-500">
                          {unseatedSeatsErrorMessage}
                        </p>
                      )}
                      <button
                        onClick={async () =>
                          addUnseatedSeats(parseInt(unseatedSeats, 10))
                        }
                        className="bg-brand mt-2 inline-flex cursor-pointer justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        {unseatedSeatsLoading ? (
                          <ButtonLoader />
                        ) : (
                          t('add-seats')
                        )}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={showDeleteSection} as={Fragment}>
        <Dialog
          as="div"
          className={`${state.theme} relative z-10`}
          onClose={() => {
            setShowDeleteSection(false)
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all dark:bg-gray-900 sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                      >
                        {t('delete-section')}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {t('delete-section-confirmation')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={() => {
                        deleteSection()
                      }}
                    >
                      {loading ? <ButtonLoader /> : t('delete-permanently')}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setShowDeleteSection(false)
                      }}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <FailureNotification
        shouldShow={showFailureMessage}
        message={t('unable-to-update-section')}
        subtitle={t('we-were-unable-to-add-section-try-again-later')}
        hide={() => setShowFailureMessage(false)}
      />

      <SuccessNotification
        shouldShow={showSuccessMessage}
        message={t('section-updated')}
        subtitle={t('we-were-able-to-update-the-section')}
        hide={() => setShowSuccessMessage(false)}
      />
      <div className="mt-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {t('section')}
        </h1>

        <form className="mt-6 gap-y-8" onSubmit={submitForm}>
          <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
            <div className="col-span-6">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                {t('name')}
              </label>
              <input
                type="text"
                name="name"
                id="name"
                autoComplete="name"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                value={name}
                onChange={updateName}
                onBlur={checkName}
              />
              {nameErrorMessage.length > 0 && (
                <p className="mt-1 text-red-500">{nameErrorMessage}</p>
              )}
            </div>
            <div className="col-span-6">
              <label
                htmlFor="entrance"
                className="block text-sm font-medium text-gray-700"
              >
                {t('entrance')}
              </label>
              <input
                type="text"
                name="entrance"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                value={entrance}
                onChange={updateEntrance}
                onBlur={checkEntrance}
              />
              {entranceErrorMessage.length > 0 && (
                <p className="mt-1 text-red-500">{entranceErrorMessage}</p>
              )}
            </div>
            <div className="col-span-6">
              <label
                htmlFor="svgId"
                className="block text-sm font-medium text-gray-700"
              >
                {t('svgId')}
              </label>
              <input
                type="text"
                name="svgId"
                id="svgId"
                autoComplete="svgId"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                value={svgId}
                onChange={updateSvgId}
                onBlur={checkSvgId}
              />
              {svgIdErrorMessage.length > 0 && (
                <p className="mt-1 text-red-500">{svgIdErrorMessage}</p>
              )}
              <div
                onClick={() => setSvgIdModal(true)}
                className="bg-brand mt-2 inline-flex cursor-pointer justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {t('choose-section')}
              </div>
            </div>
            <div className="col-span-6">
              <label
                htmlFor="default-range"
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
              >
                {t('center-of-arena')}
              </label>
              <div className="flex w-full justify-center py-4 sm:w-1/3">
                <ArrowDownIcon
                  className="h-12 w-12 text-gray-900 dark:text-white"
                  style={{ transform: `rotate(${angle * 3.6}deg)` }}
                />
              </div>
              <div className="w-full sm:w-1/3">
                <input
                  id="default-range"
                  type="range"
                  value={angle}
                  onChange={(event) => {
                    setAngle(parseInt(event.target.value, 10))
                  }}
                  className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 dark:bg-gray-700"
                />
              </div>
            </div>
            {!seatedSection && (
              <div className="col-span-6">
                {eventSk || seasonTicketSk ? (
                  <React.Fragment>
                    <label
                      htmlFor="unseated-seats"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t('unseated-seats')}
                    </label>
                    <h2 className="text-gray-900 dark:text-white">
                      {section?.unseated_seats?.toString()}
                    </h2>
                    <div
                      onClick={() => setShowAddUnseatedSeats(true)}
                      className="bg-brand mt-2 inline-flex cursor-pointer justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {t('add-unnumbered-seats')}
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <label
                      htmlFor="unseated-seats"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t('unseated-seats')}
                    </label>
                    <input
                      type="text"
                      name="unseated-seats"
                      id="unseated-seats"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                      value={unseatedSeats}
                      onChange={updateUnseatedSeats}
                      onBlur={checkUnseatedSeats}
                    />
                    {unseatedSeatsErrorMessage.length > 0 && (
                      <p className="mt-1 text-red-500">
                        {unseatedSeatsErrorMessage}
                      </p>
                    )}
                  </React.Fragment>
                )}
              </div>
            )}
            <Pricing price={price} setPrice={setPrice} />
            <div className="col-span-3">
              <div>
                <label className="text-base font-semibold text-gray-100">
                  {t('seats')}
                </label>
                <fieldset className="mt-4">
                  <legend className="sr-only">
                    {t('notification-method')}
                  </legend>
                  <div className="gap-y-4 sm:flex sm:items-center sm:gap-x-10 sm:gap-y-0">
                    <div className="flex items-center">
                      <input
                        id={'seated'}
                        name="notification-method"
                        type="radio"
                        className="text-brand-500 focus:ring-brand-500 h-4 w-4 border-gray-300"
                        value={'seated'}
                        onChange={() => setSeatedSection(true)}
                        checked={seatedSection}
                      />
                      <label
                        htmlFor={'seated'}
                        className="ml-3 block text-sm font-medium leading-6 text-gray-100"
                      >
                        {t('numbered-seat')}
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id={'standing'}
                        name="notification-method"
                        type="radio"
                        className="text-brand-500 focus:ring-brand-500 h-4 w-4 border-gray-300"
                        value={'standing'}
                        onChange={() => setSeatedSection(false)}
                        checked={!seatedSection}
                      />
                      <label
                        htmlFor={'standing'}
                        className="ml-3 block text-sm font-medium leading-6 text-gray-100"
                      >
                        Onumrerad/ståplats
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
            <div className="col-span-3">
              <label className="text-base font-semibold text-gray-100">
                Status
              </label>
              <div className="mt-4 flex items-center">
                <Switch.Group as="div" className="flex items-center">
                  <Switch
                    checked={clickable}
                    onChange={setClickable}
                    className={classNames(
                      clickable ? 'bg-brand-600' : 'bg-gray-200',
                      'focus:ring-brand-600 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2'
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        clickable ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                      )}
                    />
                  </Switch>
                  <Switch.Label as="span" className="ml-3 text-sm">
                    <span className="font-medium text-gray-100">
                      {clickable
                        ? 'Öppen för försäljning'
                        : 'Blockerad för försäljning'}
                    </span>{' '}
                  </Switch.Label>
                </Switch.Group>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-8">
            <button
              type="button"
              onClick={() => setShowDeleteSection(true)}
              className="rounded-md border border-red-300 bg-red-500 px-4 py-2 text-sm font-medium capitalize text-white shadow-sm hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t('delete')}
            </button>
            <button
              type="submit"
              className="bg-brand ml-3 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {loading ? <ButtonLoader /> : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface VenueSectionProps {
  user: User
  sid?: string | string[]
  aid?: string | string[]
}

function SectionSettings({ aid, sid }: VenueSectionProps) {
  const [loading, setLoading] = useState<boolean>(true)
  const [arena, setArena] = useState<Arena>()
  const [section, setSection] = useState<ArenaSection>()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        if (sid !== undefined && aid !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${aid}/section/${sid}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          const res: StandardAPIRequest<ArenaSection> = await response.json()
          if (res.error) {
            throw new Error(res.error.message)
          }
          setSection(res.data ?? undefined)
          let arenaResponse = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${aid}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          const arenaRes: StandardAPIRequest<Arena> = await arenaResponse.json()
          if (arenaRes.error) {
            throw new Error(arenaRes.error.message)
          }
          setArena(arenaRes.data ?? undefined)
        }
      } catch (error: any) {
        captureException(error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [aid, sid])

  if (loading) {
    return <LivedayLoader />
  }

  return <Section section={section} arena={arena} />
}

function VenueSection({ user }: VenueSectionProps) {
  const router = useRouter()
  const { aid, sid } = router.query
  const [section, setSection] = useState<ArenaSection>()
  const { t } = useTranslation('common')

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (sid !== undefined && aid !== undefined) {
          let response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/arena/${aid}/section/${sid}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await clientGetIdToken()}`,
              },
            }
          )
          const res: StandardAPIRequest<ArenaSection> = await response.json()
          if (res.error) {
            throw new Error(res.error.message)
          }
          setSection(res.data ?? undefined)
        }
      } catch (error: any) {
        captureException(error)
      }
    }

    fetchData()
  }, [aid, sid])

  if (!section) {
    return <LivedayLoader />
  }

  return (
    <>
      <Head>
        <title>{t('section-title')}</title>
      </Head>
      <VenueLayout user={user}>
        <div className="flex h-full">
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <main className="flex flex-1 flex-col overflow-hidden px-8 py-4">
              <Link
                href={`/venue/arenas/${aid}`}
                className="hidden cursor-pointer flex-row items-center pb-4 sm:flex"
              >
                <ChevronLeftIcon className="mr-0.5 h-6 w-6 text-gray-100" />
                <p className="text-gray-100">{t('back-to-all-arenas')}</p>
              </Link>
              <PrimaryVenueNavigation
                title={section?.name ?? ''}
                tabs={[
                  {
                    name: t('settings'),
                    component: (
                      <SectionSettings user={user} aid={aid} sid={sid} />
                    ),
                  },
                  {
                    name: t('seats'),
                    component: (
                      <SeatMaker section={section} aid={aid} sid={sid} />
                    ),
                  },
                ]}
              />
            </main>
          </div>
        </div>
      </VenueLayout>
    </>
  )
}

const DraggableRow = ({
  id,
  children,
}: {
  id: string
  children: React.ReactNode
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

interface SortableSeatProps {
  id: string
  seatData: ArenaSectionSeatTypes
  hooveringRight?: boolean
  hooveringLeft?: boolean
  movingRight?: boolean
}

const SortableSeat = ({
  id,
  seatData,
  hooveringRight,
  hooveringLeft,
  movingRight,
}: SortableSeatProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  if (seatData.Seat) {
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div
          className={classNames(
            hooveringRight ? 'mr-6' : 'mr-1',
            hooveringLeft ? 'ml-4' : 'ml-1',
            hooveringRight && movingRight ? '-ml-7' : '',
            hooveringLeft && movingRight === false ? '-mr-7' : '',
            'm-px flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md bg-gray-50 hover:bg-blue-700 hover:text-white'
          )}
        >
          <p className="text-center text-xs text-gray-800">
            {seatData?.Seat?.handicap_seat === true ? (
              <WheelChairIcon />
            ) : (
              <>{seatData?.Seat?.seat}</>
            )}
          </p>
        </div>
        <div
          className={classNames(
            '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm border-t border-t-gray-400 bg-gray-50'
          )}
        ></div>
      </div>
    )
  } else if (seatData.Ticket) {
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div
          className={classNames(
            hooveringRight ? 'mr-6' : 'mr-1',
            hooveringLeft ? 'ml-4' : 'ml-1',
            hooveringRight && movingRight ? '-ml-7' : '',
            hooveringLeft && movingRight === false ? '-mr-7' : '',
            seatData.Ticket?.status === 'Booked'
              ? 'bg-gray-700'
              : seatData.Ticket?.status === 'Reserved'
              ? 'bg-gray-400'
              : 'bg-gray-50',
            'm-px flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md hover:bg-blue-700 hover:text-white'
          )}
        >
          <p
            className={classNames(
              seatData.Ticket?.status === 'Booked'
                ? 'text-gray-100'
                : seatData.Ticket?.status === 'Reserved'
                ? 'text-gray-800'
                : 'text-gray-800',
              'text-center text-xs'
            )}
          >
            {seatData?.Ticket?.handicap_seat === true ? (
              <WheelChairIcon />
            ) : (
              <>{seatData?.Ticket?.seat}</>
            )}
          </p>
        </div>
        <div
          className={classNames(
            seatData.Ticket?.status === 'Booked'
              ? 'border-t border-t-gray-50 bg-gray-700'
              : seatData.Ticket?.status === 'Reserved'
              ? 'border-t border-t-gray-50 bg-gray-400'
              : 'border-t border-t-gray-400 bg-gray-50',
            '-mt-2 ml-0.5 h-2 w-7 rounded-b rounded-tl-sm rounded-tr-sm hover:bg-blue-700 hover:text-white'
          )}
        ></div>
      </div>
    )
  } else if (seatData.Space) {
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div
          className={classNames(
            'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md border border-yellow-400'
          )}
        ></div>
      </div>
    )
  } else if (seatData.Stairs) {
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div
          className={classNames(
            'm-px mx-1 flex h-8 w-6 cursor-pointer items-center justify-center rounded-t-md border border-blue-400 bg-gray-200'
          )}
        >
          <p className="text-center text-xs text-gray-800">↕</p>
        </div>
      </div>
    )
  } else {
    return null
  }
}

interface RowProps {
  id: string
  row: number
  seats: ArenaSectionSeatTypes[]
  setRow: (row: number, seats: ArenaSectionSeatTypes[]) => void
}

const Row = ({ id, row, seats, setRow }: RowProps) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [currentlyMoving, setCurrentlyMoving] = useState<number | null>(null)

  const handleDragOver = (event: any) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setCurrentlyMoving(
        seats.findIndex((_seat, idx) => `ROW-${row}-SEAT-${idx}` === active.id)
      )
      setHoverIndex(
        seats.findIndex((_seat, idx) => `ROW-${row}-SEAT-${idx}` === active.id)
      )
    } else {
      setHoverIndex(null)
    }
  }

  const handleDragEnd = (event: any) => {
    setHoverIndex(null) // Reset hover index on drag end
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = seats.findIndex(
      (_seat, idx) => `ROW-${row}-SEAT-${idx}` === active.id
    )
    const newIndex = seats.findIndex(
      (_seat, idx) => `ROW-${row}-SEAT-${idx}` === over.id
    )

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    const updatedSeats = arrayMove(seats, oldIndex, newIndex)
    setRow(row, updatedSeats)
  }

  return (
    <DndContext
      sensors={useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor))}
      modifiers={[restrictToHorizontalAxis]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <SortableContext
        id={id}
        items={seats?.map((_seat, index) => `ROW-${row}-SEAT-${index}`)}
        strategy={verticalListSortingStrategy}
      >
        {seats.filter((seat) => seat.Seat !== undefined).length > 0 && (
          <div className="flex h-11 flex-row items-center">
            <div className="flex flex-1 flex-row items-center">
              <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                {seats.find((seat) => seat.Seat !== undefined)?.Seat?.row}
              </p>
              {seats?.map((seat, index) => (
                <div key={`ROW-${row}-SEAT-${index}`}>
                  <SortableSeat
                    id={`ROW-${row}-SEAT-${index}`}
                    seatData={seat}
                    hooveringRight={
                      (currentlyMoving ?? 0) < (hoverIndex ?? 0)
                        ? hoverIndex === index
                        : hoverIndex === index + 1
                    }
                    hooveringLeft={
                      (currentlyMoving ?? 0) < (hoverIndex ?? 0)
                        ? hoverIndex === index - 1
                        : hoverIndex === index
                    }
                    movingRight={(currentlyMoving ?? 0) < (hoverIndex ?? 0)}
                  />
                </div>
              ))}
            </div>
            <p className="text-md ml-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
              {seats.find((seat) => seat.Seat !== undefined)?.Seat?.row}
            </p>
          </div>
        )}
        {seats.filter((seat) => seat.Ticket !== undefined).length > 0 && (
          <div className="flex h-11 flex-row items-center">
            <div className="flex flex-1 flex-row items-center">
              <p className="text-md mr-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
                {seats.find((seat) => seat.Ticket !== undefined)?.Ticket?.row}
              </p>
              {seats?.map((seat, index) => (
                <div key={`ROW-${row}-SEAT-${index}`}>
                  <SortableSeat
                    id={`ROW-${row}-SEAT-${index}`}
                    seatData={seat}
                    hooveringRight={
                      (currentlyMoving ?? 0) < (hoverIndex ?? 0)
                        ? hoverIndex === index
                        : hoverIndex === index + 1
                    }
                    hooveringLeft={
                      (currentlyMoving ?? 0) < (hoverIndex ?? 0)
                        ? hoverIndex === index - 1
                        : hoverIndex === index
                    }
                    movingRight={(currentlyMoving ?? 0) < (hoverIndex ?? 0)}
                  />
                </div>
              ))}
            </div>
            <p className="text-md ml-1 w-10 text-center align-middle font-semibold text-gray-900 dark:text-white">
              {seats.find((seat) => seat.Ticket !== undefined)?.Ticket?.row}
            </p>
          </div>
        )}
      </SortableContext>
    </DndContext>
  )
}

export default function Wrapper() {
  const router = useRouter()
  const [user, setUser] = useState<User>()

  const initialLoad = useCallback(async () => {
    try {
      const user = await clientUserFromCognito()
      if (user === undefined) {
        router.push('/sign_in')
      } else {
        setUser(user)
      }
    } catch (err) {
      captureException(err)
    }
  }, [router])

  useEffect(() => {
    initialLoad()
  }, [initialLoad])

  if (user === undefined) {
    return <LivedayLoader />
  }

  return (
    <>
      <VenueSection user={user} />
    </>
  )
}
